import { motion } from 'framer-motion'
import {
  Clock,
  Droplets,
  Flower2,
  Hand,
  Sparkles,
  Wind,
} from 'lucide-react'

import { AvatarChip } from '@/components/dashboard/AvatarChip'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { mapFloorRooms, mapFloorStats } from '@/lib/commandMappers'
import { cn } from '@/lib/utils'

const SERVICE_ICONS = {
  facial: Flower2,
  massage: Hand,
  nails: Sparkles,
  turnover: Wind,
}

const STATUS = {
  occupied: {
    label: 'Live',
    badge: 'bg-primary text-primary-foreground shadow-sm',
    card: 'bg-gradient-to-b from-primary/[0.06] to-card',
    bar: 'from-primary via-secondary to-gold',
    iconBg: 'bg-primary/12 text-primary',
  },
  available: {
    label: 'Ready',
    badge: 'bg-emerald-500 text-white shadow-sm',
    card: 'bg-gradient-to-b from-emerald-50/80 to-white',
    bar: 'from-emerald-400 to-emerald-500',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  turnover: {
    label: 'Prep',
    badge: 'bg-amber-500 text-white shadow-sm',
    card: 'bg-gradient-to-b from-amber-50/70 to-card',
    bar: 'from-amber-400 to-amber-500',
    iconBg: 'bg-amber-100 text-amber-700',
  },
}

export function LiveOperations() {
  const { data } = useCommandDashboard()
  const floorStats = mapFloorStats(data?.floor)
  const rooms = mapFloorRooms(data?.floor?.rooms)

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Live operations
          <span className="ml-2 font-semibold text-foreground">
            {floorStats.occupancyPct}% occupancy
          </span>
        </h2>
        <span className="flex items-center gap-1.5 text-[10px] font-semibold text-primary">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-40" />
            <span className="relative h-2 w-2 rounded-full bg-primary" />
          </span>
          Real-time
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {rooms.map((room, i) => {
          const s = STATUS[room.status]
          const ServiceIcon = room.serviceType
            ? (SERVICE_ICONS[room.serviceType] ?? Sparkles)
            : room.status === 'available'
              ? Droplets
              : Wind

          return (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              whileHover={{ y: -2 }}
              className={cn(
                'min-w-[248px] shrink-0 rounded-2xl p-4 shadow-luxury-sm transition-shadow hover:shadow-luxury',
                s.card,
              )}
              style={{ border: '1px solid hsl(262 20% 50% / 0.05)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5">
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                      s.iconBg,
                    )}
                  >
                    <ServiceIcon className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {room.fullName}
                    </p>
                    {room.status === 'occupied' && (
                      <p className="mt-0.5 text-xs font-semibold text-foreground">{room.service}</p>
                    )}
                  </div>
                </div>
                <span className={cn('rounded-lg px-2 py-0.5 text-[9px] font-bold uppercase', s.badge)}>
                  {s.label}
                </span>
              </div>

              {room.status === 'available' ? (
                <div className="mt-4 space-y-3">
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 to-white px-3 py-4 text-center shadow-inner">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(152_60%_90%/0.8),transparent_70%)]" />
                    <div className="relative">
                      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15">
                        <Sparkles className="h-5 w-5 text-emerald-600" />
                      </div>
                      <p className="text-sm font-bold text-emerald-800">{room.readiness}</p>
                      <p className="mt-0.5 text-[10px] text-emerald-600/80">{room.prepStatus}</p>
                    </div>
                  </div>
                  <dl className="space-y-1.5 text-[11px]">
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">Next booking</dt>
                      <dd className="font-bold tabular-nums">{room.nextBookingTime}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">Guest</dt>
                      <dd className="font-semibold text-foreground">{room.nextBooking}</dd>
                    </div>
                  </dl>
                </div>
              ) : room.status === 'turnover' ? (
                <div className="mt-4 space-y-2.5">
                  <p className="text-xs font-semibold text-amber-800">{room.prepStatus}</p>
                  <p className="text-[11px] text-muted-foreground">{room.readiness}</p>
                  <div className="h-1.5 overflow-hidden rounded-full bg-amber-100">
                    <div className="h-full w-[55%] rounded-full bg-gradient-to-r from-amber-400 to-amber-500" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-4 flex items-center gap-3">
                    <AvatarChip name={room.guest} size="lg" variant="guest" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold">{room.guest}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <AvatarChip name={room.therapist} size="sm" variant="therapist" />
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {room.therapist}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3.5 rounded-xl bg-muted/30 px-3 py-2.5 shadow-inner">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="font-bold text-primary">{room.occupancy}% session</span>
                      <span className="flex items-center gap-1 font-bold tabular-nums text-foreground">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {room.remaining}
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/90">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${room.occupancy}%` }}
                        transition={{ duration: 0.7, delay: 0.2 + i * 0.04 }}
                        className={cn('h-full rounded-full bg-gradient-to-r', s.bar)}
                      />
                    </div>
                    <p className="mt-1.5 text-right text-[9px] text-muted-foreground">
                      Done {room.completesAt} · {room.duration}
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}
